import { StoreIcon } from "lucide-react";

import type { FilterDefinitionType } from "@/components/@types";
import { FILTER_OPERATORS_FOR_STRINGS } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.content";
import { columnsToFilterDefinitions } from "@/components/organisms/UtilityBar/UtilityBar.utils";
import { Objects } from "@/objects";
import { toColumns } from "@/objects/@utils";

/**
 * Resolve the set of filter definitions exposed by a given GraphQL
 * object typename (e.g. ``"DocumentObject"``). The Object registry's
 * ``columns`` are the same shape the toolbar uses for filtering, so
 * the lookup picker, the canvas summary, and the toolbar all agree on
 * which fields are filterable, what type each filter is, and (for
 * reference filters) what live reference query backs the value picker.
 *
 * Returns an empty object when the typename has no registered Object,
 * which is convenient for callers that may receive a stale or unknown
 * type and want to no-op.
 */
export function getFilterDefinitionsForObject(
  objectTypename: string | undefined | null,
): Record<string, FilterDefinitionType> {
  if (!objectTypename) return {};
  const obj = Object.values(Objects).find((o) => o.typename === objectTypename);
  if (!obj) return {};
  return columnsToFilterDefinitions(toColumns(obj));
}

/**
 * Filter definitions for rule trigger conditions on bank transactions.
 * Only amount, description, merchant and financial account are valid — field
 * keys match backend condition evaluation (snake_case Django field names).
 */
export function getTriggerConditionFilterDefinitions(): Record<
  string,
  FilterDefinitionType
> {
  const all = getFilterDefinitionsForObject("TransactionObject");
  const obj = Object.values(Objects).find(
    (o) => o.typename === "TransactionObject",
  );
  const result: Record<string, FilterDefinitionType> = {};

  if (all.amount) {
    result.amount = all.amount;
  }

  const descriptionField = obj?.fieldDefinitions.find(
    (f) => f.objectKey === "description",
  );
  if (descriptionField) {
    result.description = {
      type: "text",
      name: descriptionField.label,
      operators: FILTER_OPERATORS_FOR_STRINGS,
      ReactIcon: descriptionField.ReactIcon,
    };
  }

  // Merchant rules made from triage match on it; transactions don't show it.
  result.merchant_name = {
    type: "text",
    name: "Merchant",
    operators: FILTER_OPERATORS_FOR_STRINGS,
    ReactIcon: StoreIcon,
  };

  const financialAccount = all.financialAccount;
  if (financialAccount) {
    result.financial_account = {
      ...financialAccount,
      name: "Bank account",
    };
  }

  return result;
}

/**
 * Resolve the sortable field keys for a given GraphQL object typename.
 * Mirrors the toolbar's notion of "sortable" by reading
 * ``fieldDefinitions[].fieldProps.isSortable`` from the Object registry.
 */
export function getSortableFieldsForObject(
  objectTypename: string | undefined | null,
): { key: string; label: string }[] {
  if (!objectTypename) return [];
  const obj = Object.values(Objects).find((o) => o.typename === objectTypename);
  if (!obj) return [];
  return obj.fieldDefinitions
    .filter((f) => f.fieldProps?.isSortable)
    .map((f) => ({ key: String(f.objectKey), label: f.label }));
}
